import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Form,
  Input,
  InputNumber,
  Modal,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { NativeAdoptionSchema, type NativeAdoptionValues } from '../../schemas/nativeAdoption';
import { FormActions, FormField } from '../../shared/form';
import { authFetch } from '../../utils/api';
import { getBrandLabel, type Brand } from '../../utils/tokenManager';
import { PLATFORM_LABELS } from '../../utils/constants';

interface Adoption {
  brand: Brand;
  platform: string;
  observedFrom: string;
  observedTo: string;
  windowDays: number;
  minBuildCode?: number;
  minNativeVersionName?: string;
  totalDevices: number;
  buckets: { new: number; old: number; unknown: number };
  newPercent: number | null;
  missingDeviceIdCount: number;
  distribution: Array<{
    appVersionName: string | null;
    appVersionCode: number | null;
    devices: number;
    bucket: 'new' | 'old' | 'unknown';
  }>;
}
const bucketLabels = {
  new: '達門檻',
  old: '未達門檻',
  unknown: '未知原生版本',
};

/** 獨立觀測表單使用實際 native 版本門檻，不沿用強制更新 minVersion。 */
export function NativeAdoption({
  brand,
  platform,
  onClose,
}: {
  brand: Brand;
  platform: string;
  onClose: () => void;
}) {
  const [result, setResult] = useState<Adoption | null>(null);
  const [error, setError] = useState<string | null>(null);
  const request = useRef<AbortController | null>(null);
  useEffect(() => () => request.current?.abort(), []);
  const desktop = platform === 'windows' || platform === 'macos';
  const form = useForm<NativeAdoptionValues>({
    resolver: zodResolver(NativeAdoptionSchema),
    mode: 'onBlur',
    defaultValues: { windowDays: 7 },
  });
  async function observe(values: NativeAdoptionValues) {
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    setResult(null);
    setError(null);
    const params = new URLSearchParams({
      brand,
      platform,
      windowDays: String(values.windowDays),
    });
    if (desktop)
      params.set('minNativeVersionName', values.minNativeVersionName ?? '');
    else params.set('minBuildCode', String(values.minBuildCode));
    try {
      const response = await authFetch(
        `/api/admin/app-versions/native-adoption?${params}`,
        { signal: controller.signal },
      );
      const body = await response.json();
      if (controller.signal.aborted) return;
      if (!response.ok || body.code !== 0 || !body.data)
        throw new Error(body.msg || '取得原生版本比例失敗');
      setResult(body.data);
    } catch (e) {
      if (!controller.signal.aborted)
        setError(e instanceof Error ? e.message : '取得原生版本比例失敗');
    }
  }
  return (
    <Modal
      open
      title={`原生版本比例 — ${getBrandLabel(brand)} / ${PLATFORM_LABELS[platform] || platform}`}
      onCancel={onClose}
      footer={null}
      width={760}
    >
      <Alert
        type="info"
        showIcon
        message="每個活躍裝置只取窗口內最後一次原生版本觀測，未知版本包含在分母。"
        description="請輸入已出包的原生版本門檻。提高最低版本、更新提示或 JS／OTA 版本都不能證明裝置已更新；觀測比例不會自動移除 proof。"
      />
      <FormProvider {...form}>
        <Form
          layout="vertical"
          onFinish={form.handleSubmit(observe)}
          style={{ marginTop: 16 }}
        >
          <FormField<NativeAdoptionValues>
            name="windowDays"
            label="觀測窗口（天）"
            required
            control={<InputNumber min={1} max={90} precision={0} />}
          />
          {desktop ? (
            <FormField<NativeAdoptionValues>
              name="minNativeVersionName"
              label="已出包的原生版本門檻"
              required
              control={<Input placeholder="例如 2.0.130" />}
            />
          ) : (
            <FormField<NativeAdoptionValues>
              name="minBuildCode"
              label="已出包的原生 build code 門檻"
              required
              control={
                <InputNumber
                  min={1}
                  precision={0}
                  placeholder="實際原生 build code"
                />
              }
            />
          )}
          <FormActions
            submitting={form.formState.isSubmitting}
            submitLabel="查詢比例"
          />
        </Form>
      </FormProvider>
      {error && <Alert type="error" showIcon message={error} />}
      {result && (
        <>
          <Typography.Paragraph type="secondary">
            {result.observedFrom} 至 {result.observedTo}；門檻：
            {result.minBuildCode ?? result.minNativeVersionName}
          </Typography.Paragraph>
          <Space wrap size="large">
            <Statistic title="觀測分母（含未知身份群組）" value={result.totalDevices} />
            <Statistic
              title="達門檻比例（含未知分母）"
              value={result.newPercent ?? '無法判讀'}
              precision={1}
              suffix={result.newPercent == null ? undefined : '%'}
            />
            <Statistic title="未達門檻" value={result.buckets.old} />
            <Statistic title="未知原生版本" value={result.buckets.unknown} />
          </Space>
          {result.missingDeviceIdCount > 0 && (
            <Alert
              type="warning"
              message={`有 ${result.missingDeviceIdCount} 個觀測缺裝置識別，僅按會員未知觀測群組計入 unknown，無法證明實際裝置比例；補齊識別後再判讀。`}
            />
          )}
          {result.totalDevices === 0 && (
            <Alert
              type="info"
              message="窗口內沒有此品牌／平台的裝置觀測，尚無採用比例可判讀。"
            />
          )}
          <Table
            size="small"
            pagination={false}
            dataSource={result.distribution}
            rowKey={(r) => JSON.stringify([r.appVersionName, r.appVersionCode, r.bucket])}
            columns={[
              {
                title: '原生版本',
                dataIndex: 'appVersionName',
                render: (v) => v ?? '未上報',
              },
              {
                title: '原生 build code',
                dataIndex: 'appVersionCode',
                render: (v) => v ?? '未上報',
              },
              { title: '裝置數', dataIndex: 'devices' },
              {
                title: '分類',
                dataIndex: 'bucket',
                render: (v: keyof typeof bucketLabels) => (
                  <Tag>{bucketLabels[v]}</Tag>
                ),
              },
            ]}
          />
        </>
      )}
    </Modal>
  );
}
